/**
 * ログイン画面に出す「方法」の一覧を GET /v1/platform/info から読む部分の判断（2026-09-28 creator/login-redesign）。
 * 読み込み（fetch・使い回し）は devOAuthClient.ts の loadLoginOptions、画面は pages/AuthPage.tsx・components/OAuthButtons.tsx。
 * テスト: loginOptions.test.ts（ここは node --test で読めるよう、画面・API の import を持たない）。
 *
 * パスキーでのログイン（指示書 2・リーダーの連絡 2026-09-28）:
 *   Core が /v1/platform/info に `passkeyLogin: true|false` を置く（core/login-passkey・作業中）。
 *   ボタンは **passkeyLogin === true のときだけ** 出す（無い・false・文字列の "true" 等は出さない）。
 *   押したときの流れは下の signInWithPasskey（2026-09-28 creator/passkey-login でつないだ）。
 */
import { isDevOAuthProvider, type DevOAuthProvider } from './devOAuth.ts';

export interface LoginOptions {
  /** 外部のログイン（サーバーの env が揃っているものだけ。ChatGPT・Claude は返らない = ボタンを作らない） */
  oauthProviders: DevOAuthProvider[];
  /** パスキーでのログインの口がサーバーにあるか（Core の印。true のときだけ） */
  passkeyLogin: boolean;
}

export const NO_LOGIN_OPTIONS: LoginOptions = { oauthProviders: [], passkeyLogin: false };

/** /v1/platform/info の応答から、ログイン画面に要る分だけを取り出す（知らない値は捨てる） */
export function parseLoginOptions(raw: unknown): LoginOptions {
  if (!raw || typeof raw !== 'object') return NO_LOGIN_OPTIONS;
  const r = raw as { oauthProviders?: unknown; passkeyLogin?: unknown };
  const providers = Array.isArray(r.oauthProviders) ? r.oauthProviders.filter(isDevOAuthProvider) : [];
  return { oauthProviders: [...new Set(providers)], passkeyLogin: r.passkeyLogin === true };
}

/**
 * 画面側のつなぎ込みが済んだか。false の間はサーバーが passkeyLogin: true を返してもボタンを出さない（押しても失敗するボタンを見せない）。
 * 2026-09-28 creator/passkey-login: Core の API（core/login-passkey = team/core 41c55306）につないだので true にした。
 * 戻すとき（Core の口を止める等）は false にすればボタンだけが消える（サーバーの印 passkeyLogin: false でも同じく消える）。
 */
export const PASSKEY_LOGIN_WIRED = true;

/** パスキーのボタンを出すか（サーバーの印 + 画面のつなぎ込み + ブラウザが WebAuthn を持つ） */
export function showPasskeyLogin(o: LoginOptions, browserSupports: boolean, wired: boolean = PASSKEY_LOGIN_WIRED): boolean {
  return wired && o.passkeyLogin && browserSupports;
}

// ---------------------------------------------------------------------------------------------------------
// パスキーでログイン（2026-09-28 creator/passkey-login。サーバーの正: apps/server/src/modules/platform/loginPasskeyRoutes.ts）
//   1. POST /v1/developers/passkey-login/options（本文なし・未ログイン）→ { publicKey: { challenge, rpId, allowCredentials: [], userVerification, timeout } }
//   2. navigator.credentials.get（allowCredentials 空 = 端末のパスキーから本人が選ぶ。メールは入れない）
//   3. POST /v1/developers/passkey-login { credential } → { developer, token }（パスワードのログインと同じ形）
// fetch と WebAuthn は引数（deps）で受ける: このファイルは node --test で読む（api.ts・passkey.ts は Vite 向けの import で node から読めない）。
// ---------------------------------------------------------------------------------------------------------

/** options の publicKey（passkey.ts の getPasskeyAssertion にそのまま渡す形） */
export interface PasskeyRequestJson {
  challenge: string;
  rpId: string;
  allowCredentials?: Array<{ type: 'public-key'; id: string }>;
  userVerification?: UserVerificationRequirement;
  timeout?: number;
}

export interface PasskeyLoginDeps<C, R> {
  /** ログイン前の POST（auth: false）。失敗は { code } を持つエラーを投げる（ApiError） */
  post: (path: string, body: unknown) => Promise<unknown>;
  /** navigator.credentials.get → サーバーに送る JSON（passkey.ts の getPasskeyAssertion）。取り消しは DOMException NotAllowedError */
  getAssertion: (publicKey: PasskeyRequestJson) => Promise<C>;
  /** 成功の応答の型付け（呼んだ側の Developer 型に合わせるだけ） */
  cast?: (raw: unknown) => R;
}

const codeOf = (e: unknown): string | undefined => {
  const c = (e as { code?: unknown } | null)?.code;
  return typeof c === 'string' ? c : undefined;
};

/**
 * パスキーでログインする（ボタンの onClick が呼ぶ）。成功したら { token, developer }。失敗は投げる（分け方は passkeyLoginError）。
 * 400 passkey_challenge_expired（5 分の期限切れ・一回限りの challenge を使い終えた）は options から **1 回だけ** 自動でやり直す。
 *   WHY 1 回だけか: 普通は「プロンプトを開いたまま 5 分放置した」の 1 回で直る。2 回続くのは時計・サーバーの不具合 = 回し続けず文言を出す。
 *   やり直しは認証器のプロンプトがもう一度出る（本人の操作が要る）。自動で成功させるものではない
 */
export async function signInWithPasskey<C, R = { token: string; developer: unknown }>(deps: PasskeyLoginDeps<C, R>): Promise<R> {
  for (let attempt = 0; ; attempt++) {
    const o = (await deps.post('/v1/developers/passkey-login/options', {})) as { publicKey?: PasskeyRequestJson } | null;
    if (!o?.publicKey?.challenge) throw Object.assign(new Error('passkey options missing'), { code: 'passkey_invalid' });
    const credential = await deps.getAssertion(o.publicKey);
    try {
      const r = await deps.post('/v1/developers/passkey-login', { credential });
      return deps.cast ? deps.cast(r) : (r as R);
    } catch (e) {
      if (codeOf(e) === 'passkey_challenge_expired' && attempt === 0) continue;
      throw e;
    }
  }
}

/**
 * ログインの失敗の分け方（画面の文言を決める）。
 *   'cancel'      … 本人がプロンプトを閉じた / 時間切れ（NotAllowedError・AbortError）→ **何も出さない**（指示書 1）
 *   'failed'      … 401 passkey_login_failed（未登録・出金用のパスキー・別の人…を区別しない一般の文言。サーバーも何も漏らさない）
 *                   と 400 passkey_invalid・passkey_challenge_expired（やり直しても駄目だった）
 *   'unavailable' … 404 passkey_login_unavailable（このサーバーでは使えない = 印が古い）
 *   'other'       … 通信の失敗・回数の制限など（errorText の文言に任せる）
 */
export type PasskeyLoginErrorKind = 'cancel' | 'failed' | 'unavailable' | 'other';
export function passkeyLoginError(e: unknown): PasskeyLoginErrorKind {
  const name = (e as { name?: unknown } | null)?.name;
  // DOMException か（node のテストでも DOMException はある）。名前だけで見る: ブラウザ拡張のパスキー管理が DOMException 以外を投げることがある
  if (name === 'NotAllowedError' || name === 'AbortError') return 'cancel';
  const code = codeOf(e);
  if (code === 'passkey_login_failed' || code === 'passkey_invalid' || code === 'passkey_challenge_expired') return 'failed';
  if (code === 'passkey_login_unavailable') return 'unavailable';
  if (!code) return 'failed'; // WebAuthn のほかの例外（SecurityError = RP ID 違い等）。細部を見せても本人には直せない
  return 'other';
}

/**
 * ログイン用パスキーの登録を「上限に当たった」で断られたとき（409 too_many_passkeys）の上限の数 = サーバーの details.limit。
 * それ以外のエラー・limit が数でない（古いサーバー）は null（呼ぶ側は汎用のエラー文に任せる）。
 * WHY 画面に数を持たないか（2026-10-05 console/followups）: 以前は loginPasskeyClient.ts に MAX_LOGIN_PASSKEYS = 10 を写して一覧の本数で
 *   「追加」を先に止めていた。サーバーが 50 に上げた（2026-10-04 core/limits-ratelimit・limits-audit L082）後も 10 本で止まり、
 *   11 本目を足せなかった。上限の正はサーバーだけ = 断られたときに details.limit で言う（Skills も同じ読み方）
 */
export function passkeyLimitFrom(e: unknown): number | null {
  if (codeOf(e) !== 'too_many_passkeys') return null;
  const limit = (e as { details?: { limit?: unknown } } | null)?.details?.limit;
  return typeof limit === 'number' && Number.isSafeInteger(limit) && limit > 0 ? limit : null;
}
